import type { CustomReportProps } from '..'
import { Card, Empty } from '../shared/Card'
import { displayText } from '../shared/format'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

const ALL = '(all)'

/**
 * Limits are recorded to differing precision across the spec families -- Oil
 * runs to three decimals, everything else to two -- so a fixed format would
 * either lose a digit on Oil or pad every Brix value with a meaningless zero.
 * The value is shown as recorded, trimmed of trailing zeros.
 */
function specValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return '—'
  return String(Number(n.toFixed(4)))
}

function signed(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return ''
  const shown = Number(n.toFixed(4))
  return shown > 0 ? `+${String(shown)}` : String(shown)
}

export function SpecExceptions({ result, values, setParam }: CustomReportProps) {
  const exceptions = dataset(result, 'exceptions')
  const missing = dataset(result, 'missing')
  const formulaSummary = dataset(result, 'formula_summary')
  const attributeSummary = dataset(result, 'attribute_summary')

  const formula =
    typeof values.formula === 'string' && values.formula !== ALL ? values.formula : null
  const specType = typeof values.type === 'string' && values.type !== ALL ? values.type : null

  const comparedFormulas = formulaSummary.filter((row) => count(row.attributes_compared) > 0)
  const offendingFormulas = formulaSummary.filter((row) => count(row.exceptions) > 0)
  const offendingSkus = new Set(exceptions.map((row) => text(row.item_id) ?? '')).size

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {exceptions.length.toLocaleString()}{' '}
        {exceptions.length === 1 ? 'exception' : 'exceptions'} across{' '}
        {offendingSkus.toLocaleString()} {offendingSkus === 1 ? 'SKU' : 'SKUs'} in{' '}
        {offendingFormulas.length.toLocaleString()} of {comparedFormulas.length.toLocaleString()}{' '}
        comparable {comparedFormulas.length === 1 ? 'formula' : 'formulas'}
        {formula === null ? '' : ` — ${formula}`}
        {specType === null ? '' : ` — ${specType}`}
      </p>

      <Card
        title="Specification exceptions"
        subtitle={
          'Each row is one SKU whose limit differs from the value most of its formula ' +
          'siblings hold. Select a formula to narrow the list.'
        }
      >
        {exceptions.length === 0 ? (
          <Empty label="Every SKU agrees with its formula for these filters." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="detail-table spec-exception-table">
              <thead>
                <tr>
                  <th>Formula</th>
                  <th>Specification</th>
                  <th>Item ID</th>
                  <th>Item</th>
                  <th>Type</th>
                  <th className="cell-number">This SKU</th>
                  <th className="cell-number">Formula</th>
                  <th className="cell-number">Diff</th>
                  <th className="cell-number">Agreeing</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((row, index) => {
                  const verdict = text(row.verdict)
                  const agreeing = count(row.agreeing_skus)
                  const compared = count(row.compared_skus)
                  return (
                    <tr
                      key={`${displayText(text(row.formula_code))}-${displayText(
                        text(row.attribute),
                      )}-${displayText(text(row.item_id))}-${String(index)}`}
                      className="detail-row"
                    >
                      <td className="cell-mono">
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => {
                            setParam('formula', displayText(text(row.formula_code)))
                          }}
                        >
                          {displayText(text(row.formula_code))}
                        </button>
                      </td>
                      <td>{displayText(text(row.attribute))}</td>
                      <td className="cell-mono">{displayText(text(row.item_id))}</td>
                      <td className="cell-truncate" title={displayText(text(row.item_name))}>
                        {displayText(text(row.item_name))}
                      </td>
                      <td>{displayText(text(row.type_value))}</td>
                      <td className="cell-number is-exception">{specValue(row.sku_value)}</td>
                      <td className="cell-number">
                        {verdict === 'no_majority' ? (
                          <span className="spec-no-majority" title="The SKUs split evenly">
                            no majority
                          </span>
                        ) : (
                          specValue(row.majority_value)
                        )}
                      </td>
                      <td className="cell-number">{signed(row.difference)}</td>
                      <td className="cell-number">
                        {agreeing.toLocaleString()} / {compared.toLocaleString()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Specifications by exception count"
        subtitle="Which limit disagrees most often across all formulas."
      >
        {attributeSummary.length === 0 ? (
          <Empty label="No disagreements for these filters." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="detail-table spec-summary-table">
              <thead>
                <tr>
                  <th>Specification</th>
                  <th className="cell-number">Formulas</th>
                  <th className="cell-number">Exceptions</th>
                </tr>
              </thead>
              <tbody>
                {attributeSummary.map((row) => (
                  <tr key={displayText(text(row.attribute))} className="detail-row">
                    <td>{displayText(text(row.attribute))}</td>
                    <td className="cell-number">
                      {count(row.formulas_disagreeing).toLocaleString()}
                    </td>
                    <td className="cell-number">{count(row.exceptions).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormulaTable rows={comparedFormulas} setParam={setParam} />

      <Card
        title="Limits a SKU does not declare"
        subtitle={
          'The SKU is silent about a limit its formula siblings agree on. A gap in the ' +
          'spec system rather than a contradiction, so it is listed apart from the ' +
          'exceptions above.'
        }
      >
        {missing.length === 0 ? (
          <Empty label="Every SKU declares every limit its formula uses." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="detail-table spec-summary-table">
              <thead>
                <tr>
                  <th>Formula</th>
                  <th>Specification</th>
                  <th>Item ID</th>
                  <th>Item</th>
                  <th className="cell-number">Formula value</th>
                  <th className="cell-number">SKUs with a value</th>
                </tr>
              </thead>
              <tbody>
                {missing.map((row, index) => (
                  <tr
                    key={`${displayText(text(row.formula_code))}-${displayText(
                      text(row.attribute),
                    )}-${displayText(text(row.item_id))}-${String(index)}`}
                    className="detail-row"
                  >
                    <td className="cell-mono">{displayText(text(row.formula_code))}</td>
                    <td>{displayText(text(row.attribute))}</td>
                    <td className="cell-mono">{displayText(text(row.item_id))}</td>
                    <td className="cell-truncate" title={displayText(text(row.item_name))}>
                      {displayText(text(row.item_name))}
                    </td>
                    <td className="cell-number">{specValue(row.majority_value)}</td>
                    <td className="cell-number">{count(row.agreeing_skus).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function FormulaTable({
  rows,
  setParam,
}: {
  rows: Keyed[]
  setParam: (name: string, value: string) => void
}) {
  return (
    <Card
      title="Formulas compared"
      subtitle="Formulas with more than one SKU declaring a limit. A formula with one SKU has nothing to disagree with and is not listed."
    >
      {rows.length === 0 ? (
        <Empty label="No comparable formulas for these filters." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="detail-table spec-summary-table">
            <thead>
              <tr>
                <th>Formula</th>
                <th>Ingredient statement</th>
                <th className="cell-number">SKUs</th>
                <th className="cell-number">Specs compared</th>
                <th className="cell-number">Specs disagreeing</th>
                <th className="cell-number">Exceptions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const exceptions = count(row.exceptions)
                return (
                  <tr
                    key={displayText(text(row.formula_code))}
                    className={exceptions > 0 ? 'detail-row has-exceptions' : 'detail-row'}
                  >
                    <td className="cell-mono">
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => {
                          setParam('formula', displayText(text(row.formula_code)))
                        }}
                      >
                        {displayText(text(row.formula_code))}
                      </button>
                    </td>
                    <td
                      className="cell-truncate"
                      title={displayText(text(row.ingredient_statement))}
                    >
                      {displayText(text(row.ingredient_statement))}
                    </td>
                    <td className="cell-number">{count(row.skus).toLocaleString()}</td>
                    <td className="cell-number">
                      {count(row.attributes_compared).toLocaleString()}
                    </td>
                    <td className="cell-number">
                      {count(row.attributes_disagreeing).toLocaleString()}
                    </td>
                    <td className="cell-number">{exceptions.toLocaleString()}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
